"use client";

import type { PlaceholderValues, Rating } from "@homeinstead/schemas";
import type { FormInterstitialStep, RenderableFormItem } from "./form-types";

import { memo, useCallback, useEffect, useRef } from "react";
import { toContentAlignment } from "./form-step";
import { LeadFormStep } from "./LeadFormStep";
import { LeadFormVisibleItems } from "./LeadFormVisibleItems";
import { defaultLeadFormStyleClasses } from "./lead-form-styles";
import { StepContext } from "./StepContext";
import { useTrackLeadFormEvent } from "./useTrackLeadFormEvent";

export type LeadFormInterstitialStepItemProps = {
  step: FormInterstitialStep;
  isActive: boolean;
  isPending: boolean;
  officeRating?: Omit<Rating, "_type">;
  phoneNumber?: string;
  placeholderValues?: PlaceholderValues;
  onContinue: () => void;
};

function LeadFormInterstitialStepItemInner({
  step,
  isActive,
  officeRating,
  phoneNumber,
  placeholderValues,
  onContinue,
}: LeadFormInterstitialStepItemProps) {
  const trackLeadForm = useTrackLeadFormEvent();
  // Guards against a double-advance race: if the auto-advance timer and a
  // manual Continue click fire simultaneously within the same activation window,
  // only the first call goes through. Reset when the step deactivates so the
  // timer re-arms correctly if the user navigates back to this step.
  const hasAutoAdvancedRef = useRef(false);

  const handleContinue = useCallback(() => {
    if (hasAutoAdvancedRef.current) {
      return;
    }
    hasAutoAdvancedRef.current = true;
    onContinue();
  }, [onContinue]);

  useEffect(() => {
    if (!isActive) {
      hasAutoAdvancedRef.current = false;
      return;
    }
    if (!step.autoAdvance) {
      return;
    }

    const delay = step.autoAdvanceDelay ?? 5000;
    const timerId = setTimeout(() => {
      trackLeadForm?.leadFormStep({ stepName: step.metricsLabel ?? step.id });
      handleContinue();
    }, delay);
    return () => clearTimeout(timerId);
  }, [
    handleContinue,
    isActive,
    step.autoAdvance,
    step.autoAdvanceDelay,
    step.id,
    step.metricsLabel,
    step.stepperDisplay?.duration,
    trackLeadForm,
  ]);

  return (
    <StepContext value={{ isActive }}>
      <LeadFormStep
        className={defaultLeadFormStyleClasses.stepBase}
        contentAlignment={toContentAlignment(step)}
        isVisible={isActive}
        verticalPadding="none"
      >
        <div className={defaultLeadFormStyleClasses.stepContentBase}>
          <LeadFormVisibleItems
            contentMaxWidth="none"
            officeRating={officeRating}
            onContinue={handleContinue}
            phoneNumber={phoneNumber}
            placeholderValues={placeholderValues}
            stepId={step.id}
            stepName={step.metricsLabel ?? step.id}
            visibleItems={(step.inputs ?? []) as RenderableFormItem[]}
          />
        </div>
      </LeadFormStep>
    </StepContext>
  );
}

export const LeadFormInterstitialStepItem = memo(
  LeadFormInterstitialStepItemInner,
);
